import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, FileCheck2, Minus, Plus, ShoppingBag } from "lucide-react";
import { api, money } from "../lib/api";
import { useCart } from "../context/CartContext";
import { Vial } from "../components/Vial";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const { addItem } = useCart();

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => setProduct(false));
  }, [id]);

  if (product === null) {
    return <div className="flex min-h-screen items-center justify-center font-mono text-xs tracking-[0.3em] text-slate-500" data-testid="product-loading">LOADING...</div>;
  }
  if (product === false) {
    return <div className="flex min-h-screen items-center justify-center text-slate-400" data-testid="product-not-found">Product not found.</div>;
  }

  return (
    <div className="spotlight mx-auto max-w-7xl px-5 pb-24 pt-32 sm:px-8" data-testid="product-detail-page">
      <Link to="/products" data-testid="back-to-shop-link" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] text-slate-500 transition-colors duration-200 hover:text-[#E8E9EB]">
        <ArrowLeft className="h-3.5 w-3.5" /> BACK TO SHOP
      </Link>

      <div className="mt-10 grid items-start gap-14 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="tracing relative flex justify-center rounded-3xl bg-[#0C0C0F] py-20"
        >
          <Vial code={product.code} pid={product.id} className="w-full max-w-sm" />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}>
          <div className="flex items-center gap-3">
            <span className="font-mono text-[11px] tracking-[0.3em] text-[#E8E9EB]">{product.category.toUpperCase()}</span>
            <span className="rounded-full border border-white/10 px-2.5 py-1 font-mono text-[10px] text-slate-400">{product.code}</span>
            <span
              data-testid="detail-stock-status"
              className={`flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 font-mono text-[9px] tracking-[0.1em] ${
                product.in_stock !== false
                  ? "border-emerald-400/25 text-emerald-400"
                  : "border-red-400/25 text-red-400"
              }`}
            >
              <span className={`h-1 w-1 rounded-full ${product.in_stock !== false ? "bg-emerald-400" : "bg-red-400"}`} />
              {product.in_stock !== false ? "IN STOCK" : "OUT OF STOCK"}
            </span>
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl" data-testid="product-detail-name">
            {product.name}
          </h1>
          <p className="mt-2 text-base text-slate-400 sm:text-lg">{product.subtitle}</p>
          <p className="mt-6 max-w-lg text-sm leading-relaxed text-slate-500">{product.description}</p>

          <div className="mt-8 grid grid-cols-3 gap-3">
            {[["PURITY", product.purity], ["FORMAT", product.dose], ...(product.coa ? [["BATCH", product.coa.batch_number]] : [])].map(([k, v]) => (
              <div key={k} className="glass rounded-xl p-3.5">
                <div className="font-mono text-[9px] tracking-[0.2em] text-slate-500">{k}</div>
                <div className="mt-1.5 font-mono text-xs text-white">{v}</div>
              </div>
            ))}
          </div>

          <ul className="mt-8 flex flex-col gap-3">
            {product.benefits.map((b) => (
              <li key={b} className="flex items-start gap-3 text-sm text-slate-300">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#E8E9EB]" /> {b}
              </li>
            ))}
          </ul>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-4 rounded-full border border-white/10 px-4 py-2">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="detail-qty-minus" className="text-slate-400 hover:text-white"><Minus className="h-4 w-4" /></button>
              <span className="w-6 text-center font-mono text-white" data-testid="detail-qty">{qty}</span>
              <button onClick={() => setQty((q) => Math.min(product.stock ?? 20, q + 1))} data-testid="detail-qty-plus" className="text-slate-400 hover:text-white"><Plus className="h-4 w-4" /></button>
            </div>
            <button
              onClick={() => addItem(product, qty)}
              disabled={product.in_stock === false}
              data-testid="detail-add-to-cart"
              className="inline-flex items-center gap-2 rounded-full bg-[#6E7683] px-7 py-3.5 font-mono text-xs tracking-[0.2em] text-white transition-colors duration-200 hover:bg-[#E8E9EB] hover:text-[#050811] disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-[#6E7683] disabled:hover:text-white"
            >
              <ShoppingBag className="h-4 w-4" /> {product.in_stock === false ? "OUT OF STOCK" : `ADD TO CART - ${money(product.price * qty)}`}
            </button>
            {product.in_stock !== false && product.stock != null && product.stock <= 5 && (
              <span className="font-mono text-[10px] tracking-[0.15em] text-amber-400" data-testid="detail-low-stock">
                ONLY {product.stock} LEFT
              </span>
            )}
          </div>

          {product.coa && (
            <Link
              to={`/coa/${product.id}`}
              data-testid="detail-coa-link"
              className="glass mt-8 flex items-center justify-between rounded-xl p-4 transition-colors duration-200 hover:border-[#E8E9EB]/40"
            >
              <div className="flex items-center gap-3">
                <FileCheck2 className="h-5 w-5 text-[#E8E9EB]" />
                <div>
                  <div className="text-sm font-medium text-white">Certificate of Analysis</div>
                  <div className="font-mono text-[10px] tracking-[0.15em] text-slate-500">BATCH {product.coa.batch_number} / VERIFIED {product.coa.test_date || product.coa.analysis_conducted}</div>
                </div>
              </div>
              <span className="font-mono text-[10px] tracking-[0.2em] text-[#E8E9EB]">VIEW COA</span>
            </Link>
          )}
        </motion.div>
      </div>
    </div>
  );
}
